import { Block } from '../block';
import { Bounding } from '../../../core/models/bounding';
import { Element } from '../../../equipment/models/elements/element';
import { EquipmentBase } from '../equipment-base';
import { Extension } from '../../../equipment/models/elements/extension';
import { Figure } from '../../../equipment/models/figures/figure';
import { GlobalSettings } from '../../../core/models/globalSettings';
import { ICanPutProductsResult } from '../canPutProductsResult';
import { Point } from '../../../core/models/point';
import { ProductErrors, ProductGroup } from '../product-group';
import { ProductGroupRotation } from '../product-group-rotation';

import { Utils } from '../../../core/services/utils.service';

export class HoleElement extends Element {

	get anchorYCoordinate(): number {
		return this.shapes[0].begin.y;
	}

	get layoutBoundingX(): Bounding {
		const bounding: Bounding = this.getBoundingX();

		return {
			min: Utils.round(bounding.max - bounding.min),
			max: Utils.round(bounding.max - bounding.min),
		}
	}

	get layoutBoundingY(): Bounding {
		const bounding: Bounding = this.getBoundingY();

		return {
			min: bounding.min,
			max: bounding.max
		}
	}

	get products(): Array<Array<ProductGroup>> {
		const product: ProductGroup = this.stand.products.find((pg: ProductGroup) => pg.element == this);
		return product ? [[product]] : [];
	}

	get sortedProducts(): Array<ProductGroup> {
		const perforationElements: Array<Element> = this.stand.allElements
			.filter((el: Element) => el.depend == this.depend);
		const productsOnPerforation: Array<ProductGroup> = perforationElements
			.map((el: Element) => this.stand.products.find((pg: ProductGroup) => pg.element == el))
			.flat();

		return productsOnPerforation.sort((a: ProductGroup, b: ProductGroup) => {
			return b.element.shapes[0].begin.y - a.element.shapes[0].begin.y ||
				a.element.shapes[0].begin.x - b.element.shapes[0].begin.x;
		});
	}

	constructor(element: Element) {
		super(element.parent, element.shapes, element.extensions, element.depend, element.id, element.idx);
	}

	public addProduct(pg: ProductGroup) {
		pg.element = this;
		pg.placementHeight = 1;
		pg.angle = GlobalSettings.DEFAULT_PRODUCT_GROUP_ANGLE;
		pg.begin = {
			x: Utils.round(this.getBoundingX().min - (pg.ItemWidth - this.shapes[0].width) / 2),
			y: Utils.round(this.getBoundingY().min - pg.ItemHeight),
			z: this.getBoundingZ().max
		};
	}

	canDrop(product: ProductGroup): boolean {
		return !this.products.length && !product.isLooseForm;
	}

	copy(): HoleElement {
		const shapes: Array<Figure> = this.shapes.map((shape: Figure) => shape.copy()),
			extensions: Array<Extension<any>> = this.extensions.map((extension: Extension<any>) => extension.copy()),
			element: Element = new Element(this.parent, shapes, extensions, this.depend, this.id, this.idx);

		return new HoleElement(element);
	}

	normalize(stand: EquipmentBase): void {
		const products: Array<ProductGroup> = stand.products.filter(p => p.element == this),
			beginZ: number = this.depend.getBoundingZ().max;

		for (let i = products.length - 1; i >= 0; i--) {
			const errors: { [key in ProductErrors]?: boolean } = {
				[ProductErrors.LooseOnShelf]: products[i].isLooseForm
			};
			products[i].begin.z = beginZ + products[i].Depth;
			products[i].begin.x = products[i].element.shapes[0].begin.x -
				(products[i].ItemWidth - products[i].element.shapes[0].width) / 2;
			products[i].begin.y = products[i].element.getBoundingY().min - products[i].ItemHeight;
			products[i].error = Object.values(errors).some((error: boolean) => error);
			products[i].errorTypes = Object.keys(errors).filter((key: ProductErrors) => errors[key]);
		}
	}

	public canPutProductGroups(products: Array<ProductGroup>, point: Point): ICanPutProductsResult {
		if (products.some((pg: ProductGroup) => pg.element == this)) return { success: false };

		if (this.products.length) {
			return {
				success: false,
				message: {
					messageText: 'DROP_PRODUCT_ERROR_3',
					translateParams: {}
				}
			};
		}

		if (products.some((pg: ProductGroup) => pg.isLooseForm)) {
			return {
				success: false,
				message: {
					messageText: 'WEIGHT_PRODUCT_ON_SHELF_ERROR',
					translateParams: {}
				}
			};
		}

		const block: Block = this.stand.findBlock(point.x, point.y, products[0].Width);

		for (let product of products) {
			if (block && (product.block != block.id || !block.id)) {
				if (!block.isProductSuitable(product.product)) {
					return {
						success: false,
						message: {
							messageText: 'BLOCK_PRODUCT_ERROR',
							translateParams: { blockName: block.name }
						}
					};
				}
			}
		}

		return { success: true }
	}

	public putProductGroups(products: Array<ProductGroup>, list: Array<Array<ProductGroup>>, point: Point): void {
		const freeHoles: Array<HoleElement> = this.getFreeHoles(this.depend);

		products.forEach((pg: ProductGroup, index: number) => {
			let element: HoleElement = freeHoles[index];

			if (!element) {
				return this.stand.getDroppedElement(point.x, Utils.round(this.stand.Height - point.y))
					.putProductGroups(products, list, { x: point.x, y: point.y });
			}

			pg.Element = element;
			pg.dependProduct = null;
			pg.qty = 1;
			pg.placementDepth = 1;
			pg.placementHeight = 1;

			this.stand.moveProductToTheEnd(pg);
		});
	}

	private getFreeHoles(element: Element): Array<HoleElement> {
		let sortedHoles: Array<HoleElement> = this.stand.allElements
			.filter((el: Element) => el.depend == element)
			.sort((a: Element, b: Element) => {
				return b.shapes[0].begin.y - a.shapes[0].begin.y || a.shapes[0].begin.x - b.shapes[0].begin.x;
			}) as Array<HoleElement>;

		const thisHoleIndex: number = sortedHoles.findIndex((el: Element) => el === this);

		return sortedHoles.filter((el: HoleElement, index: number) => !el.products.length && index >= thisHoleIndex);
	}
}
